import { Injectable, Injector } from '@angular/core';
import {
    FiOnboardingReportType,
    FiOnboardingReportWidgetType,
} from '@fi-app/fi-onboarding/fi-onboarding-report/fi-onboarding-report-enums';
import { INameIdObj } from '@fi-shared/fi-shared-interfaces';
import {
    ISelectedFilterItem,
    ISharingMenuData,
} from '@fi-shared/fi-items-list/fi-items-list-filter/fi-items-list-filter/fi-items-list-filter';
import { ILightWeightObject } from '@fi-shared/models';
import { FiFilterType } from '@fi-shared/fi-items-list/fi-items-list-filter/fi-filter-type-enum';
import { WidgetTableFilterMenuType } from '@fi-shared/checklist/fi-checklist2-report/components/shared/widget-table-filter/widget-table-filter-menu-type';
import {
    IOnboardingForReport,
    IOnboardingReportAvailableItems,
    IOnboardingReportWidgetFilter,
} from '@fi-app/fi-onboarding/fi-onboarding-report/fi-onboarding-report';
import { FiReportTableFilterHelperBase } from '@fi-shared/fi-report-table-filter/fi-report-table-filter-helper-base';
import { FiOnboardingCategories } from '@fi-app/fi-onboarding/fi-onboarding-consts';
import { FiOnboardingCategoryType } from '@fi-app/fi-onboarding/fi-onboarding-enums';
import { isDefined } from '@fi-shared/utils';
import { FiCustomerDataService } from '@fi-shared/fi-shared-services/fi-customer-data.service';
import { IReportAvailableItemsByData } from '@fi-shared/fi-report-table-filter/fi-report-filter-base';
import { fiItemsFlatten } from '@fi-shared/utils/fi-items-flatten';

interface IFilterMenuCreateArgs {
    reportType: FiOnboardingReportType;
    availableItems: IOnboardingReportAvailableItems;
    reportWidgetType: FiOnboardingReportWidgetType;
    availableItemsByDataFn: () => IReportAvailableItemsByData;
}

@Injectable()
export class FiOnboardingReportTableFilterHelperService extends FiReportTableFilterHelperBase {
    constructor(
        injector: Injector,
        private fiCustomerDataService: FiCustomerDataService,
    ) {
        super(injector);
    }

    getFilterMenusData(args: IFilterMenuCreateArgs): ISharingMenuData<INameIdObj>[] {
        const menusData: ISharingMenuData<INameIdObj>[] = [];

        menusData.push(this.getOnboardingsMenu(args));
        if (this.fiCustomerDataService.isSynchronizedOnboardingEnabled()) {
            menusData.push(this.getOnboardingCategoriesMenu(args));
        }
        menusData.push(
            this.getBusinessUnitsMenu({
                items: args.availableItems.BusinessUnits,
                isAvailable:
                    this.isFilterMenuAvailable(FiFilterType.BusinessUnit, args.reportType, args.reportWidgetType) &&
                    args.availableItems.BusinessUnits.length > 1,
                isOptionHidden: (businessUnit) => {
                    const isHidden =
                        isDefined(args.availableItemsByDataFn()?.BusinessUnitIds) &&
                        !args.availableItemsByDataFn()?.BusinessUnitIds.includes(businessUnit.Id);
                    return isHidden;
                },
            }),
        );
        menusData.push(
            this.getAreasMenu({
                areas: args.availableItems.Areas,
                isAvailable:
                    this.isFilterMenuAvailable(FiFilterType.Area, args.reportType, args.reportWidgetType) &&
                    fiItemsFlatten(args.availableItems.Areas).length > 1,
                isOptionHidden: (area) => {
                    const availableAreaIds = args.availableItemsByDataFn()?.AreaIds;
                    const isHidden =
                        isDefined(availableAreaIds) &&
                        !availableAreaIds.includes(area.Id) &&
                        !availableAreaIds.includes(area.ParentId);
                    return isHidden;
                },
            }),
        );
        menusData.push(
            this.getBusinessAreasMenu({
                items: args.availableItems.BusinessAreas,
                isAvailable:
                    this.isFilterMenuAvailable(FiFilterType.BusinessArea, args.reportType, args.reportWidgetType) &&
                    args.availableItems.BusinessAreas.length > 1 &&
                    args.availableItems.BusinessUnits.length > 1,
                isOptionHidden: (businessArea) => {
                    const isHidden =
                        isDefined(args.availableItemsByDataFn()?.BusinessAreaIds) &&
                        !args.availableItemsByDataFn()?.BusinessAreaIds.includes(businessArea.Id);
                    return isHidden;
                },
            }),
        );
        menusData.push(
            this.getUsersMenu({
                users: args.availableItems.Users,
                isAvailable: this.isFilterMenuAvailable(FiFilterType.User, args.reportType, args.reportWidgetType),
                labelTextId: 'form.participants',
                isOptionHidden: (user) => {
                    const isHidden =
                        isDefined(args.availableItemsByDataFn()?.UserIds) &&
                        !args.availableItemsByDataFn()?.UserIds.includes(user.Id);
                    return isHidden;
                },
            }),
        );

        return menusData;
    }

    private getOnboardingsMenu(args: IFilterMenuCreateArgs): ISharingMenuData<IOnboardingForReport> {
        return {
            label: this.appTextService.get('onboarding.onboardings'),
            menuItems: args.availableItems.Onboardings,
            displayFieldPropertyName: 'Name',
            filterType: FiFilterType.Onboarding,
            selectedItemIds: [],
            isOptionHidden: (item) => {
                if (args.reportWidgetType === FiOnboardingReportWidgetType.ParticipantsOnboarded) {
                    return !item.HasOnboardedParticipants;
                }
                return !item.IsActive;
            },
            isAvailable: () =>
                this.isFilterMenuAvailable(FiFilterType.Onboarding, args.reportType, args.reportWidgetType),
        };
    }

    private getOnboardingCategoriesMenu(args: IFilterMenuCreateArgs): ISharingMenuData<ILightWeightObject> {
        return {
            label: this.appTextService.get('common.category'),
            menuItems: FiOnboardingCategories.filter((item) => item.id !== FiOnboardingCategoryType.Synchronized).map(
                (item) => ({
                    Id: item.id,
                    Name: this.appTextService.get(item.textId),
                }),
            ),
            displayFieldPropertyName: 'Name',
            filterType: FiFilterType.OnboardingCategory,
            selectedItemIds: [],
            isSearchByNameInputHidden: true,
            isAvailable: () =>
                this.isFilterMenuAvailable(FiFilterType.OnboardingCategory, args.reportType, args.reportWidgetType),
        };
    }

    canDeselectItem(filterType: FiFilterType, reportWidgetType: FiOnboardingReportWidgetType): boolean {
        if (filterType === FiFilterType.Onboarding) {
            return ![
                FiOnboardingReportWidgetType.OnboardingLocations,
                FiOnboardingReportWidgetType.OnboardingParticipants,
            ].includes(reportWidgetType);
        }

        if (filterType === FiFilterType.BusinessUnit) {
            return reportWidgetType !== FiOnboardingReportWidgetType.OnboardingParticipants;
        }

        return true;
    }

    getFilterMenuType(
        filterType: FiFilterType,
        reportType: FiOnboardingReportType,
        reportWidgetType: FiOnboardingReportWidgetType,
    ): WidgetTableFilterMenuType {
        const wt = FiOnboardingReportWidgetType;
        if (filterType === FiFilterType.Onboarding) {
            if ([wt.OnboardingLocations, wt.OnboardingParticipants].includes(reportWidgetType)) {
                return WidgetTableFilterMenuType.SingleSelectOption;
            }
        }
        if (filterType === FiFilterType.BusinessUnit) {
            if ([wt.OnboardingParticipants].includes(reportWidgetType)) {
                return WidgetTableFilterMenuType.SingleSelectOption;
            }
        }
        if (filterType === FiFilterType.Area) {
            return WidgetTableFilterMenuType.AlignedWithChildren;
        }
        return WidgetTableFilterMenuType.MultiSelectOption;
    }

    isFilterMenuAvailable(
        filterType: FiFilterType,
        reportType: FiOnboardingReportType,
        reportWidgetType: FiOnboardingReportWidgetType,
    ): boolean {
        const wt = FiOnboardingReportWidgetType;
        const rt = FiOnboardingReportType;
        if (filterType === FiFilterType.OnboardingCategory) {
            if (
                [
                    wt.OnboardingLocations,
                    wt.OnboardingParticipants,
                    wt.ParticipantsOnboarded,
                    wt.RecentActivities,
                ].includes(reportWidgetType)
            ) {
                return false;
            }
            if ([rt.Independent, rt.Synchronised].includes(reportType)) {
                return false;
            }
        }
        if (filterType === FiFilterType.Area) {
            if ([wt.OnboardingParticipants].includes(reportWidgetType)) {
                return false;
            }
        }
        if (filterType === FiFilterType.BusinessArea) {
            if ([wt.OnboardingParticipants].includes(reportWidgetType)) {
                return false;
            }
        }
        if (filterType === FiFilterType.User) {
            if ([wt.OnboardingCompletion, wt.OnboardingLocations, wt.RecentActivities].includes(reportWidgetType)) {
                return false;
            }
        }
        // todo wip
        return true;
    }

    getSelectedFilterItemsFromFilter(
        currentFilter: IOnboardingReportWidgetFilter,
        availableItems: IOnboardingReportAvailableItems,
    ): ISelectedFilterItem[] {
        const selectedOnboardings = this.getSelectedFilterItemsFromAvailableItems(
            currentFilter.OnboardingIds,
            availableItems.Onboardings,
            FiFilterType.Onboarding,
        );
        const selectedCategories = this.getSelectedFilterItemsFromEnum(
            currentFilter.OnboardingCategories,
            FiOnboardingCategories,
            FiFilterType.OnboardingCategory,
        );
        const selectedBusinessUnits = this.getSelectedFilterItemsFromAvailableItems(
            currentFilter.BusinessUnitIds,
            availableItems.BusinessUnits,
            FiFilterType.BusinessUnit,
        );
        const selectedAreas = this.getSelectedFilterItemsFromAvailableItems(
            currentFilter.AreaIds,
            fiItemsFlatten(availableItems.Areas),
            FiFilterType.Area,
        );
        const selectedBusinessAreas = this.getSelectedFilterItemsFromAvailableItems(
            currentFilter.BusinessAreaIds,
            availableItems.BusinessAreas,
            FiFilterType.BusinessArea,
        );
        const selectedUsers = this.getSelectedFilterItemsFromAvailableUsers(
            currentFilter.UserIds,
            availableItems.Users,
        );
        return [
            ...selectedOnboardings,
            ...selectedCategories,
            ...selectedAreas,
            ...selectedBusinessUnits,
            ...selectedBusinessAreas,
            ...selectedUsers,
        ];
    }

    getFilterFromSelectedItems(
        selectedFilterItemGroups: { [key: number]: ISelectedFilterItem[] },
        filter: IOnboardingReportWidgetFilter,
    ) {
        const newFilter: IOnboardingReportWidgetFilter = {
            ...filter,
            OnboardingIds: this.getValuesFromSelectedFilterItems(selectedFilterItemGroups[FiFilterType.Onboarding]),
            OnboardingCategories: this.getValuesFromSelectedFilterItems(
                selectedFilterItemGroups[FiFilterType.OnboardingCategory],
            ),
            AreaIds: this.getValuesFromSelectedFilterItems(selectedFilterItemGroups[FiFilterType.Area]),
            BusinessUnitIds: this.getValuesFromSelectedFilterItems(selectedFilterItemGroups[FiFilterType.BusinessUnit]),
            BusinessAreaIds: this.getValuesFromSelectedFilterItems(selectedFilterItemGroups[FiFilterType.BusinessArea]),
            UserIds: this.getValuesFromSelectedFilterItems(selectedFilterItemGroups[FiFilterType.User]),
        };
        Object.keys(newFilter).forEach((key) => {
            if (!newFilter[key as keyof IOnboardingReportWidgetFilter]?.length) {
                delete newFilter[key as keyof IOnboardingReportWidgetFilter];
            }
        });

        return newFilter;
    }
}
